import React from 'react';
import { BgImage, Img } from '../components/Img';
import { IMG } from '../images';
import { Link } from 'react-router-dom';
import { Home, Droplets, Wind, Sun, Shield, CheckCircle } from 'lucide-react';

const Premium = () => {
  const services = [
    {
      icon: <Droplets size={48} />,
      title: 'Luxury Window Cleaning',
      description: 'Crystal-clear glass cleaning for multi-storey homes, glass walls, skylights and architectural glazing',
      features: [
        'Multi-storey window cleaning',
        'Floor-to-ceiling glass walls',
        'Skylights and roof windows',
        'Architectural glazing features',
        'Streak-free pure water cleaning',
        'Delicate surface treatment'
      ]
    },
    {
      icon: <Wind size={48} />,
      title: 'Exterior House Washing',
      description: 'Gentle cleaning methods designed for premium materials including stone, marble, render and timber',
      features: [
        'Natural stone cleaning',
        'Marble and premium surfaces',
        'Rendered facades',
        'Timber cladding care',
        'Architectural concrete',
        'Pressure washing for durable surfaces'
      ]
    },
    {
      icon: <Shield size={48} />,
      title: 'Glass Balustrades & Pool Fences',
      description: 'Professional cleaning and restoration of glass railings, pool fencing and terrace balustrades',
      features: [
        'Glass railing cleaning',
        'Pool glass fencing',
        'Terrace balustrades',
        'Watermark removal',
        'Protective coating application',
        'Regular maintenance plans'
      ]
    },
    {
      icon: <Sun size={48} />,
      title: 'Roof & Difficult Access Cleaning',
      description: 'Specialized cleaning for hard-to-reach areas including roof windows, solar panels and glass atriums',
      features: [
        'Roof window cleaning',
        'Solar panel cleaning',
        'High terrace maintenance',
        'Glass atrium cleaning',
        'Gutter cleaning',
        'Difficult access areas'
      ]
    }
  ];

  const premiumFeatures = [
    'Discretion and professionalism',
    'Attention to detail and premium finish',
    'Minimal disruption to your property',
    'Safe and careful handling of all surfaces',
    'Flexible scheduling around your needs',
    'Complete care for high-value properties',
    'Expert knowledge of premium materials',
    'Before and after photo documentation'
  ];

  return (
    <div>
      {/* Hero Section */}
      <section 
        className="hero" 
        style={{
          minHeight: '70vh'
        }}
      >
        <BgImage image={IMG.premiumHero} position="50% 50%" priority />
        <div className="hero-content">
          <span style={{ 
            display: 'inline-block',
            background: 'var(--safety-orange)',
            color: 'var(--white)',
            padding: '8px 20px',
            fontSize: '0.9rem',
            fontWeight: 700,
            letterSpacing: '1px',
            marginBottom: '1rem'
          }}>
            LUXURY HOMES
          </span>
          <h1>Luxury Home Exterior Services</h1>
          <p>Professional rope access services for high-value private residences and architecturally unique homes</p>
          <div className="hero-buttons">
            <Link to="/contact" className="btn-primary">Request a Quote</Link>
            <a href="tel:0426920301" className="btn-secondary">Call for Consultation</a>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <h2>Rope Access Services for Luxury Homes</h2>
            <p style={{ maxWidth: '900px', margin: '0 auto' }}>
              ProClimb Solutions also provides specialised rope access services for high-value private residences and architecturally unique homes. Luxury homes often feature large glass façades, steep architectural designs and difficult-to-reach areas. Our rope access techniques allow us to maintain these properties without intrusive equipment or scaffolding.
            </p>
          </div>

          <div style={{ 
            background: 'var(--light-gray)', 
            padding: '3rem', 
            border: '1px solid var(--border-color)',
            marginTop: '3rem'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
              <Home size={40} style={{ color: 'var(--navy-blue)' }} />
              <h3 style={{ fontSize: '1.8rem', color: 'var(--navy-blue)', margin: 0 }}>
                Why Choose Rope Access for Your Luxury Home?
              </h3>
            </div>
            <ul style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
              gap: '1rem',
              listStyle: 'none',
              padding: 0 
            }}>
              <li style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
                <CheckCircle size={20} style={{ color: 'var(--safety-orange)', flexShrink: 0, marginTop: '3px' }} />
                <span>No scaffolding cluttering your property</span>
              </li>
              <li style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
                <CheckCircle size={20} style={{ color: 'var(--safety-orange)', flexShrink: 0, marginTop: '3px' }} />
                <span>Access steep or complex architectural features</span>
              </li>
              <li style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
                <CheckCircle size={20} style={{ color: 'var(--safety-orange)', flexShrink: 0, marginTop: '3px' }} />
                <span>Minimal disruption to landscaping and gardens</span>
              </li>
              <li style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
                <CheckCircle size={20} style={{ color: 'var(--safety-orange)', flexShrink: 0, marginTop: '3px' }} />
                <span>Safe cleaning of multi-storey glass walls</span>
              </li>
              <li style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
                <CheckCircle size={20} style={{ color: 'var(--safety-orange)', flexShrink: 0, marginTop: '3px' }} />
                <span>Discreet and professional service</span>
              </li>
              <li style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
                <CheckCircle size={20} style={{ color: 'var(--safety-orange)', flexShrink: 0, marginTop: '3px' }} />
                <span>Expert care for premium finishes</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="section bg-light">
        <div className="container">
          <div className="section-header">
            <h2>Services for Private Homes</h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem' }}>
            {services.map((service, index) => (
              <div 
                key={index}
                style={{
                  background: 'var(--white)',
                  padding: '2.5rem',
                  border: '1px solid var(--border-color)',
                  transition: 'all 0.3s'
                }}
                className="service-card"
              >
                <div style={{ color: 'var(--navy-blue)', marginBottom: '1.5rem' }}>
                  {service.icon}
                </div>
                <h3 style={{ color: 'var(--navy-blue)', fontSize: '1.5rem', marginBottom: '1rem' }}>
                  {service.title}
                </h3>
                <p style={{ color: 'var(--steel-gray)', marginBottom: '1.5rem' }}>
                  {service.description}
                </p>
                <ul style={{ listStyle: 'none', padding: 0 }}>
                  {service.features.map((feature, idx) => (
                    <li key={idx} style={{ 
                      display: 'flex', 
                      gap: '0.5rem', 
                      marginBottom: '0.5rem',
                      color: 'var(--steel-gray)',
                      fontSize: '0.95rem'
                    }}>
                      <CheckCircle size={16} style={{ color: 'var(--safety-orange)', flexShrink: 0, marginTop: '3px' }} />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Premium Service Experience */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <h2>The Luxury Home Experience</h2>
            <p>What sets our luxury home service apart</p>
          </div>
          
          {/* Add ProClimb tech working photo */}
          <div style={{ 
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '3rem',
            alignItems: 'center',
            marginBottom: '3rem'
          }}>
            <div>
              <Img image={IMG.glassroof} alt={IMG.glassroof.alt} sizes="(max-width: 900px) 100vw, 560px" />
            </div>
            <div>
              <h3 style={{ color: 'var(--navy-blue)', fontSize: '1.8rem', marginBottom: '1rem' }}>
                Professional ProClimb Team
              </h3>
              <p style={{ fontSize: '1.1rem', lineHeight: 1.8, color: 'var(--steel-gray)', marginBottom: '1rem' }}>
                Our certified technicians bring decades of experience to every luxury home project. 
                Wearing ProClimb Solutions branded uniforms, our team maintains the highest standards of 
                professionalism and expertise.
              </p>
              <p style={{ fontSize: '1.1rem', lineHeight: 1.8, color: 'var(--steel-gray)' }}>
                We understand that luxury properties require exceptional care, discretion, and attention to detail. 
                That's why we assign our most experienced Level 3 IRATA certified technicians to luxury home work.
              </p>
            </div>
          </div>
          
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
            gap: '1.5rem',
            maxWidth: '1000px',
            margin: '0 auto'
          }}>
            {premiumFeatures.map((feature, index) => (
              <div 
                key={index}
                style={{
                  display: 'flex',
                  gap: '1rem',
                  alignItems: 'center',
                  padding: '1.5rem',
                  background: 'var(--light-gray)',
                  border: '1px solid var(--border-color)'
                }}
              >
                <CheckCircle size={24} style={{ color: 'var(--safety-orange)', flexShrink: 0 }} />
                <span style={{ fontSize: '1.05rem', color: 'var(--industrial-black)' }}>{feature}</span>
              </div>
            ))}
          </div>
        </div>
        
        {/* Responsive CSS */}
        <style jsx>{`
          @media (max-width: 768px) {
            div[style*="grid-template-columns: 1fr 1fr"] {
              grid-template-columns: 1fr !important;
            }
          }
        `}</style>
      </section>

      {/* Gallery */}
      <section className="section bg-light">
        <div className="container">
          <div className="section-header reveal">
            <h2>The Properties We Specialise In</h2>
            <p>Waterfront estates, architect-designed homes and high-value residences. Images are illustrative.</p>
          </div>
          <div className="gallery">
            {[
              { image: IMG.lux1, alt: 'Luxury home with infinity pool at dusk' },
              { image: IMG.lux2, alt: 'Waterfront residence with terraced gardens' },
              { image: IMG.lux3, alt: 'Modern home with timber soffit and pool' },
              { image: IMG.lux4, alt: 'Mediterranean-style luxury home exterior' },
            ].map((g) => (
              <figure key={g.alt} className="gallery-item reveal">
                <Img image={g.image} alt={g.alt} sizes="(max-width: 768px) 50vw, 33vw" />
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* Who We Work With */}
      <section className="section">
        <div className="container" style={{ textAlign: 'center' }}>
          <h2 style={{ marginBottom: '2rem' }}>We Work With</h2>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
            gap: '2rem',
            maxWidth: '900px',
            margin: '0 auto'
          }}>
            <div>
              <h3 style={{ color: 'var(--navy-blue)', marginBottom: '0.5rem' }}>Homeowners</h3>
              <p style={{ color: 'var(--steel-gray)' }}>Direct service for luxury home owners</p>
            </div>
            <div>
              <h3 style={{ color: 'var(--navy-blue)', marginBottom: '0.5rem' }}>Architects</h3>
              <p style={{ color: 'var(--steel-gray)' }}>Maintenance for architectural projects</p>
            </div>
            <div>
              <h3 style={{ color: 'var(--navy-blue)', marginBottom: '0.5rem' }}>Property Managers</h3>
              <p style={{ color: 'var(--steel-gray)' }}>Care for high-value estates</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section bg-dark">
        <div className="container" style={{ textAlign: 'center', color: 'var(--white)' }}>
          <h2 style={{ color: 'var(--white)', marginBottom: '1.5rem' }}>
            Rope Access Built for Luxury Homes
          </h2>
          <p style={{ fontSize: '1.2rem', marginBottom: '2rem', color: '#e5e7eb', maxWidth: '700px', margin: '0 auto 2rem' }}>
            Contact us for a confidential consultation and tailored quote for your luxury property
          </p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/contact" className="btn-primary">Request a Quote</Link>
            <a href="tel:0426920301" className="btn-secondary">Call: 0426 920 301</a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Premium;
